import React, { useCallback, useMemo, useState } from 'react';
import { useBoqProjectItems } from '../hooks/useBoqProjectItems';
import { useBoqLibrary } from '../hooks/useBoqLibrary';
import { useBoqMeasurements, sumMeasureForLine } from '../hooks/useBoqMeasurements';
import { useBoqClauses } from '../hooks/useBoqClauses';
import { useBoqScopeParties } from '../hooks/useBoqScopeParties';
import { useBoqAutosave } from '../hooks/useBoqAutosave';
import {
  RotateCcw,
  RefreshCw,
  Upload,
  ClipboardPaste,
  FileSpreadsheet,
  FileText,
  FilePlus2,
  Plus,
  Users,
  ChevronsDownUp,
  ChevronsUpDown,
} from 'lucide-react';
import {
  BrioCard,
  BrioButton,
  BrioEmptyState,
} from '../../components/BrioComponents';
import { ToolbarIcon, ToolbarDivider } from './ToolbarIcon';
import AddProjectItemModal from './AddProjectItemModal';
import AddLumpItemModal from './AddLumpItemModal';
import AddAllowanceLineModal from './AddAllowanceLineModal';
import AddLineChooserModal from './AddLineChooserModal';
import ImportItemsModal from './ImportItemsModal';
import PasteRowsModal from './PasteRowsModal';
import ProjectItemsGrid from './ProjectItemsGrid';
import ScopeModal from './ScopeModal';
import MeasurementSheet from './MeasurementSheet';
import LinkMeasurementsModal from './LinkMeasurementsModal';
import ImportMeasurementsModal from './ImportMeasurementsModal';
import RateLibraryModal from './RateLibraryModal';
import SdRatePickerModal from './SdRatePickerModal';
import { isAreaBasis } from '../utils/sdRateStats';
import SeedFromTemplateModal from './SeedFromTemplateModal';
import { useBoqTaxonomies } from '../hooks/useBoqTaxonomies';
import { useBoqSubtradeCatalogue } from '../hooks/useBoqSubtradeCatalogue';
import { canonicalTradesFor } from '../utils/canonicalTrades';
import { exportItemsToXlsx } from '../utils/projectItemsXlsx';
import { exportMeasurementSheet } from '../utils/measurementSheetXlsx';
import { renderBillHtml, openBillForPrint } from '../utils/renderBillHtml';

// Take-off bases the measurement sheet can bill on. Excludes the display-only
// zone gross-area bases (carpet / built-up / construction) that SD allowance
// lines use — those have no take-off, so they're cleared when a take-off starts.
const TAKEOFF_BASES = new Set(['count', 'running_m', 'plan_area', 'elevation_area', 'volume']);

function downloadBlob(buffer, filename) {
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function ProjectItemsSection({ project, zones, zoneTree, ensureMirror }) {
  const { items, loading, error, refresh, createItem, createMany, updateItem, deleteItem, duplicateItem, setScopeForItems, selectOptionInGroup, undo, canUndo } =
    useBoqProjectItems(project.boqId);
  const { versions, loading: libLoading, tradeGroupByItem, chapterByItem, subtradeByItem } = useBoqLibrary();
  // New lines take active library versions only; an archived version stays on
  // the lines that already use it but is not offered for new ones.
  const pickableVersions = useMemo(() => versions.filter((v) => v.status === 'active'), [versions]);
  const { clauses } = useBoqClauses(project.boqId);
  const scopeApi = useBoqScopeParties(project.boqId);
  const autosave = useBoqAutosave(project.boqId);
  const {
    measurements,
    createMeasurement,
    createMeasurementsMany,
    createMeasurementsWithLinks,
    updateMeasurement,
    deleteMeasurement,
    deleteMeasurements,
    linkMeasurement,
    setLinkDeduction,
    unlinkMeasurement,
  } = useBoqMeasurements(project.boqId);
  const [showAdd, setShowAdd] = useState(false);
  const [showLump, setShowLump] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showPaste, setShowPaste] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [measureLineId, setMeasureLineId] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showImportMeas, setShowImportMeas] = useState(false);
  const [ratesLineId, setRatesLineId] = useState(null);
  const [billView, setBillView] = useState('quoted');
  const [showScope, setShowScope] = useState(false);
  const [showSeed, setShowSeed] = useState(false);
  const [showAllowance, setShowAllowance] = useState(false);
  const [showChooser, setShowChooser] = useState(false);
  // Collapse / expand-all controls surfaced by the grid (drawer state lives in
  // the grid; the button sits in this toolbar, next to Refresh).
  const [collapseCtl, setCollapseCtl] = useState(null);
  const { taxonomies: seedTaxonomies, refresh: refreshTaxonomies } = useBoqTaxonomies();
  // Canonical SD sub-trade catalogue — the pickable list behind "Add allowance
  // line". Read once; shared regardless of the project's stage (TD reuses SD).
  const { entries: subtradeCatalogue, loading: catalogueLoading, refresh: refreshCatalogue } = useBoqSubtradeCatalogue();

  // Canonical 11-trade chapters for SD / TD bills — drives the grid's trade
  // grouping so the Bill of Quantities is organised like the Summary (and the
  // Tender stage follows the SD template). Null for DD/GFC and other stages.
  const canonicalTrades = useMemo(
    () => canonicalTradesFor(seedTaxonomies, project.schemeStage),
    [seedTaxonomies, project.schemeStage]
  );

  // Open the Seed-from-template modal. If the project doesn't yet have a BOQ
  // mirror row, bootstrap it first so the RPC has a target.
  const handleOpenSeed = useCallback(async () => {
    if (!project.hasMirror) {
      setBootstrapping(true);
      const r = await ensureMirror();
      setBootstrapping(false);
      if (!r.success) return;
    }
    setShowSeed(true);
  }, [project.hasMirror, ensureMirror]);

  const codeForItem = useCallback((id) => {
    const it = items.find((x) => x.id === id);
    return it?.version?.fullCode || '?';
  }, [items]);

  const zonesById = useMemo(() => {
    const m = new Map();
    (zones || []).forEach((z) => m.set(z.id, z));
    return m;
  }, [zones]);

  // measurement count per BOQ line, for the grid's Take-off button + the
  // read-only-quantity rule.
  const countByItem = useMemo(() => {
    const m = new Map();
    measurements.forEach((meas) => {
      meas.itemIds.forEach((id) => m.set(id, (m.get(id) || 0) + 1));
    });
    return m;
  }, [measurements]);

  const itemsForGrid = useMemo(
    () => items.map((it) => ({ ...it, measurementCount: countByItem.get(it.id) || 0 })),
    [items, countByItem]
  );

  // Trades (first two chars of the full code) for the bulk scope-assign picker.
  const tradeOf = (it) => (it.version?.fullCode || '').slice(0, 2) || it.trade || 'Other';
  const trades = useMemo(() => {
    const m = new Map();
    items.forEach((it) => { const c = tradeOf(it); m.set(c, (m.get(c) || 0) + 1); });
    return [...m.entries()].map(([code, count]) => ({ code, count })).sort((a, b) => a.code.localeCompare(b.code));
  }, [items]);

  // Bulk-assign a scope party to all lines, or just one trade.
  const handleAssignScope = useCallback(async (scopePartyId, trade) => {
    const ids = items.filter((it) => trade == null || tradeOf(it) === trade).map((it) => it.id);
    if (ids.length === 0) return { success: true, count: 0 };
    await autosave.beforeBulk('Auto — before scope assign');
    const res = await setScopeForItems(ids, scopePartyId);
    if (res.success) autosave.markDirty();
    return res;
  }, [items, setScopeForItems, autosave]);

  // Recompute a line's quantity from its linked measurements + its basis,
  // then persist (without polluting the line's undo stack — the take-off
  // edits are the real undoable actions).
  // `source` lets callers pass the post-mutation measurement list so the sum
  // reflects a just-completed import / create / edit (the `measurements`
  // closure is stale until the next render).
  const recompute = useCallback(async (itemId, basis, source) => {
    if (!basis) return;
    const list = source || measurements;
    const q = sumMeasureForLine(list, basis, itemId) ?? 0;
    await updateItem(itemId, { quantity: q }, { recordUndo: false });
    autosave.markDirty();
  }, [measurements, updateItem, autosave]);

  // Single update entry point for the grid. Basis changes also trigger a
  // quantity recompute.
  const handleGridUpdate = useCallback(async (id, payload) => {
    const result = await updateItem(id, payload);
    if (result.success) {
      autosave.markDirty();
      if (payload.measureBasis !== undefined) await recompute(id, payload.measureBasis || null);
    }
    return result;
  }, [updateItem, recompute, autosave]);

  // Wrap item add + bulk import/paste with autosave (before bulk + mark dirty).
  const createItemAutosaved = useCallback(async (input) => {
    const r = await createItem(input);
    if (r.success) autosave.markDirty();
    return r;
  }, [createItem, autosave]);

  const createManyAutosaved = useCallback(async (inputs) => {
    await autosave.beforeBulk('Auto — before import/paste');
    const r = await createMany(inputs);
    if (r.success) autosave.markDirty();
    return r;
  }, [createMany, autosave]);

  const deleteItemDirty = useCallback(async (id) => {
    const r = await deleteItem(id);
    if (r.success) autosave.markDirty();
    return r;
  }, [deleteItem, autosave]);

  const duplicateItemDirty = useCallback(async (id) => {
    const r = await duplicateItem(id);
    if (r.success) autosave.markDirty();
    return r;
  }, [duplicateItem, autosave]);

  const handleSelectOption = useCallback(async (id) => {
    const r = await selectOptionInGroup(id);
    if (r.success) autosave.markDirty();
    else if (r.error) {
      // eslint-disable-next-line no-alert
      window.alert(r.error);
    }
    return r;
  }, [selectOptionInGroup, autosave]);

  // Open a line's take-off sheet, so a few quick calculations can be done
  // in-app instead of in Excel. We deliberately don't change line_type: the DB
  // requires measured lines to carry a library version (which SD allowance
  // placeholders lack), and an allowance with a take-off is still a valid
  // allowance — the take-off just derives its quantity. A display-only gross-
  // area basis (the SD allowance default) can't be taken off, so it's cleared
  // here to give the sheet a clean take-off basis picker; a real take-off basis
  // already set is left untouched.
  const handleOpenTakeoff = useCallback(async (id) => {
    const line = items.find((it) => it.id === id);
    if (line && line.measureBasis && !TAKEOFF_BASES.has(line.measureBasis)) {
      const r = await updateItem(id, { measureBasis: null });
      if (!r.success) {
        // eslint-disable-next-line no-alert
        if (r.error) window.alert(r.error);
        return r;
      }
      autosave.markDirty();
    }
    setMeasureLineId(id);
    return { success: true };
  }, [items, updateItem, autosave]);

  // Measurement mutations recompute the affected line afterwards.
  const lineBasisById = useMemo(() => {
    const m = new Map();
    items.forEach((it) => m.set(it.id, it.measureBasis || null));
    return m;
  }, [items]);

  const handleMeasurementCreate = useCallback(async (itemId, input) => {
    const result = await createMeasurement(itemId, input);
    if (result.success) {
      await recompute(itemId, lineBasisById.get(itemId), [...measurements, result.measurement]);
    }
    return result;
  }, [createMeasurement, recompute, lineBasisById, measurements]);

  const handleMeasurementUpdate = useCallback(async (id, input) => {
    const result = await updateMeasurement(id, input);
    if (result.success && measureLineId) {
      const next = measurements.map((m) => (m.id === id ? result.measurement : m));
      await recompute(measureLineId, lineBasisById.get(measureLineId), next);
    }
    return result;
  }, [updateMeasurement, recompute, measureLineId, lineBasisById, measurements]);

  const handleBasisChange = useCallback(async (itemId, basis) => {
    await updateItem(itemId, { measureBasis: basis || null });
    await recompute(itemId, basis || null);
  }, [updateItem, recompute]);

  // Row "×" in the take-off sheet: unlink from this line. If the measurement
  // then feeds no line, delete it (orphan cleanup).
  const handleRemoveRow = useCallback(async (measurementId) => {
    if (!measureLineId) return { success: false };
    const m = measurements.find((x) => x.id === measurementId);
    const res = await unlinkMeasurement(measureLineId, measurementId);
    if (res.success) {
      const remaining = (m?.itemIds || []).filter((x) => x !== measureLineId);
      if (remaining.length === 0) await deleteMeasurement(measurementId);
      const next = measurements.filter((x) => x.id !== measurementId);
      await recompute(measureLineId, lineBasisById.get(measureLineId), next);
    }
    return res;
  }, [measureLineId, measurements, unlinkMeasurement, deleteMeasurement, recompute, lineBasisById]);

  const handleLinkExisting = useCallback(async (measurementIds, isDeduction = false) => {
    if (!measureLineId) return { success: false };
    for (const mid of measurementIds) {
      // eslint-disable-next-line no-await-in-loop
      const r = await linkMeasurement(measureLineId, mid, isDeduction);
      if (!r.success) return r;
    }
    const idSet = new Set(measurementIds);
    const next = measurements.map((m) =>
      idSet.has(m.id) && !m.links.some((l) => l.itemId === measureLineId)
        ? { ...m, links: [...m.links, { itemId: measureLineId, isDeduction }], itemIds: [...m.itemIds, measureLineId] }
        : m);
    await recompute(measureLineId, lineBasisById.get(measureLineId), next);
    return { success: true };
  }, [measureLineId, measurements, linkMeasurement, recompute, lineBasisById]);

  // Toggle whether a measurement deducts from the current line.
  const handleToggleDeduct = useCallback(async (measurementId, isDeduction) => {
    if (!measureLineId) return { success: false };
    const res = await setLinkDeduction(measureLineId, measurementId, isDeduction);
    if (res.success) {
      const next = measurements.map((m) => (m.id === measurementId
        ? { ...m, links: m.links.map((l) => (l.itemId === measureLineId ? { ...l, isDeduction } : l)) }
        : m));
      await recompute(measureLineId, lineBasisById.get(measureLineId), next);
    }
    return res;
  }, [measureLineId, measurements, setLinkDeduction, recompute, lineBasisById]);

  const handleExportMeasurements = useCallback(async (line) => {
    const linked = measurements.filter((m) => m.itemIds.includes(line.id));
    const buffer = await exportMeasurementSheet({
      line, basis: line.measureBasis || '', measurements: linked, zonesById,
    });
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safe = (line.version?.fullCode || 'line').replace(/[^A-Za-z0-9_.-]/g, '_');
    a.href = url; a.download = `takeoff-${safe}.xlsx`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }, [measurements, zonesById]);

  const handleImportMeasurements = useCallback(async (inputs) => {
    if (!measureLineId) return { success: false };
    await autosave.beforeBulk('Auto — before measurement import');
    const res = await createMeasurementsMany(measureLineId, inputs);
    if (res.success) {
      const next = [...measurements, ...(res.measurements || [])];
      await recompute(measureLineId, lineBasisById.get(measureLineId), next);
    }
    return res;
  }, [measureLineId, measurements, createMeasurementsMany, recompute, lineBasisById, autosave]);

  // Recompute several lines at once from an explicit measurement list.
  const recomputeMany = useCallback(async (itemIds, source) => {
    for (const id of new Set(itemIds)) {
      // eslint-disable-next-line no-await-in-loop
      await recompute(id, lineBasisById.get(id), source);
    }
  }, [recompute, lineBasisById]);

  // Duplicate / paste: create measurements carrying their own line links.
  const handleCreateRows = useCallback(async (inputs) => {
    const res = await createMeasurementsWithLinks(inputs);
    if (res.success) {
      const next = [...measurements, ...(res.measurements || [])];
      const affected = new Set();
      (res.measurements || []).forEach((m) => m.itemIds.forEach((id) => affected.add(id)));
      await recomputeMany([...affected], next);
    }
    return res;
  }, [createMeasurementsWithLinks, measurements, recomputeMany]);

  // Excel-style cell-range paste: apply per-cell field overrides to existing
  // rows and create new measurements for overflow, then recompute once.
  const handleApplyPaste = useCallback(async ({ updates, newRows }) => {
    let working = measurements;
    for (const u of updates) {
      const cur = working.find((m) => m.id === u.id);
      if (!cur) continue;
      const merged = {
        description: cur.description, nr: cur.nr, length: cur.length, breadth: cur.breadth, height: cur.height,
        projectZoneId: cur.projectZoneId, level: cur.level, gridRef: cur.gridRef, locationNote: cur.locationNote,
        ...u.fields,
      };
      // eslint-disable-next-line no-await-in-loop
      const res = await updateMeasurement(u.id, merged);
      if (res.success) working = working.map((m) => (m.id === u.id ? res.measurement : m));
    }
    if (newRows.length && measureLineId) {
      const inputs = newRows.map((o) => ({ ...o, itemIds: [measureLineId] }));
      const res = await createMeasurementsWithLinks(inputs);
      if (res.success) working = [...working, ...res.measurements];
    }
    const affected = new Set();
    updates.forEach((u) => { const m = working.find((x) => x.id === u.id); if (m) m.itemIds.forEach((id) => affected.add(id)); });
    if (newRows.length && measureLineId) affected.add(measureLineId);
    await recomputeMany([...affected], working);
    return { success: true };
  }, [measurements, updateMeasurement, createMeasurementsWithLinks, recomputeMany, measureLineId]);

  // Cut / delete-selected: bulk delete then recompute every line they fed.
  const handleDeleteMany = useCallback(async (ids) => {
    await autosave.beforeBulk('Auto — before measurement bulk delete');
    const idSet = new Set(ids);
    const affected = new Set();
    measurements.forEach((m) => { if (idSet.has(m.id)) m.itemIds.forEach((id) => affected.add(id)); });
    const res = await deleteMeasurements(ids);
    if (res.success) {
      const next = measurements.filter((m) => !idSet.has(m.id));
      await recomputeMany([...affected], next);
    }
    return res;
  }, [deleteMeasurements, measurements, recomputeMany, autosave]);

  const measureLine = useMemo(
    () => items.find((it) => it.id === measureLineId) || null,
    [items, measureLineId]
  );
  const ratesLine = useMemo(
    () => items.find((it) => it.id === ratesLineId) || null,
    [items, ratesLineId]
  );

  const handleOpenAdd = async () => {
    if (!project.hasMirror) {
      setBootstrapping(true);
      const result = await ensureMirror();
      setBootstrapping(false);
      if (!result.success) return;
    }
    setShowAdd(true);
  };

  const handleOpenAllowance = async () => {
    if (!project.hasMirror) {
      setBootstrapping(true);
      const result = await ensureMirror();
      setBootstrapping(false);
      if (!result.success) return;
    }
    setShowAllowance(true);
  };

  const handleOpenLump = async () => {
    if (!project.hasMirror) {
      setBootstrapping(true);
      const result = await ensureMirror();
      setBootstrapping(false);
      if (!result.success) return;
    }
    setShowLump(true);
  };

  // Route the "Add line" chooser to the matching flow (each does its own
  // mirror bootstrap).
  const handleChooseLineType = (type) => {
    setShowChooser(false);
    if (type === 'library') handleOpenAdd();
    else if (type === 'subtrade') handleOpenAllowance();
    else if (type === 'lump') handleOpenLump();
  };

  const handleOpenImport = async () => {
    if (!project.hasMirror) {
      setBootstrapping(true);
      const result = await ensureMirror();
      setBootstrapping(false);
      if (!result.success) return;
    }
    setShowImport(true);
  };

  const handleOpenPaste = async () => {
    if (!project.hasMirror) {
      setBootstrapping(true);
      const result = await ensureMirror();
      setBootstrapping(false);
      if (!result.success) return;
    }
    setShowPaste(true);
  };

  const handleUndo = async () => {
    const result = await undo();
    if (!result.success && result.error && result.error !== 'Nothing to undo.') {
      // eslint-disable-next-line no-alert
      window.alert(result.error);
    }
  };

  const handleExport = async () => {
    const buffer = await exportItemsToXlsx({
      projectName: project.name,
      projectCode: project.projectCode,
      currency: project.currency,
      items,
      zonesById,
    });
    const safe = (project.projectCode || 'boq').replace(/[^A-Za-z0-9_-]/g, '_');
    downloadBlob(buffer, `${safe}-boq-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleExportPdf = () => {
    const html = renderBillHtml(items, {
      projectName: project.name,
      projectCode: project.projectCode,
      currency: project.currency,
      basis: billView,
      clauses,
    });
    if (!openBillForPrint(html)) {
      // eslint-disable-next-line no-alert
      window.alert('Allow pop-ups for this site to open the printable bill.');
    }
  };

  const totalLines = items.length;

  return (
    <BrioCard>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontFamily: 'Raleway, sans-serif', fontSize: '1.05rem' }}>
          Items <span style={{ color: 'var(--brio-text-muted)', fontWeight: 400, fontSize: '0.9rem' }}>· {totalLines} line{totalLines === 1 ? '' : 's'}</span>
        </h3>
        <div style={{ display: 'flex', gap: '0.15rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <ToolbarIcon onClick={handleUndo} disabled={!canUndo} tooltip={canUndo ? 'Undo last change' : 'Nothing to undo'}>
            <RotateCcw size={15} />
          </ToolbarIcon>
          <ToolbarIcon onClick={refresh} disabled={loading || !project.boqId} tooltip="Refresh">
            <RefreshCw size={15} />
          </ToolbarIcon>
          <ToolbarIcon
            onClick={() => { if (collapseCtl) (collapseCtl.allCollapsed ? collapseCtl.expandAll() : collapseCtl.collapseAll()); }}
            disabled={!collapseCtl || !collapseCtl.grouped || !collapseCtl.anyCollapsible}
            tooltip={collapseCtl && collapseCtl.allCollapsed ? 'Expand all trades' : 'Collapse all trades'}
          >
            {collapseCtl && collapseCtl.allCollapsed ? <ChevronsUpDown size={15} /> : <ChevronsDownUp size={15} />}
          </ToolbarIcon>
          <ToolbarDivider />
          <ToolbarIcon onClick={handleOpenImport} disabled={bootstrapping || libLoading} tooltip="Import Excel">
            <Upload size={15} />
          </ToolbarIcon>
          <ToolbarIcon onClick={handleOpenPaste} disabled={bootstrapping || libLoading} tooltip="Paste rows">
            <ClipboardPaste size={15} />
          </ToolbarIcon>
          <ToolbarDivider />
          <ToolbarIcon onClick={handleExport} disabled={totalLines === 0} tooltip={totalLines === 0 ? 'No lines to export' : 'Export Excel (.xlsx)'}>
            <FileSpreadsheet size={15} />
          </ToolbarIcon>
          <ToolbarIcon onClick={handleExportPdf} disabled={totalLines === 0} tooltip={totalLines === 0 ? 'No lines to export' : `Printable ${billView} bill (PDF)`}>
            <FileText size={15} />
          </ToolbarIcon>
          <ToolbarDivider />
          <ToolbarIcon onClick={() => setShowScope(true)} active={scopeApi.parties.length > 0} tooltip="Scope / responsibility">
            <Users size={15} />
          </ToolbarIcon>
          <ToolbarDivider />
          <BrioButton
            variant="primary"
            onClick={() => setShowChooser(true)}
            disabled={bootstrapping || libLoading || catalogueLoading}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.7rem', fontSize: '0.82rem' }}
          >
            <Plus size={14} /> {bootstrapping ? 'Setting up…' : 'Add line'}
          </BrioButton>
        </div>
      </div>

      {!project.boqId ? (
        <BrioEmptyState>
          <div style={{ marginBottom: '0.5rem' }}>No BOQ set up yet for this project.</div>
          <div style={{ fontSize: '0.82rem', marginBottom: '0.85rem' }}>
            Add the first line above, import from Excel, or seed the canonical estimate template below.
          </div>
          <BrioButton
            variant="primary"
            onClick={handleOpenSeed}
            disabled={bootstrapping}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
          >
            <FilePlus2 size={14} /> {bootstrapping ? 'Setting up…' : 'Seed from template'}
          </BrioButton>
        </BrioEmptyState>
      ) : loading || libLoading ? (
        <div style={{ color: 'var(--brio-text-muted)', padding: '0.5rem 0' }}>Loading lines…</div>
      ) : error ? (
        <div style={{ color: '#a94442', padding: '0.5rem 0' }}>{error}</div>
      ) : items.length === 0 ? (
        <BrioEmptyState>
          <div style={{ marginBottom: '0.5rem' }}>No BOQ lines yet.</div>
          <div style={{ fontSize: '0.82rem', marginBottom: '0.85rem' }}>
            Add lines individually, import from Excel, or seed the canonical estimate template (62 placeholder rows you fill in).
          </div>
          <BrioButton
            variant="primary"
            onClick={handleOpenSeed}
            disabled={bootstrapping}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
          >
            <FilePlus2 size={14} /> {bootstrapping ? 'Setting up…' : 'Seed from template'}
          </BrioButton>
        </BrioEmptyState>
      ) : (
        <>
          <ProjectItemsGrid
            items={itemsForGrid}
            zoneTree={zoneTree}
            scopeParties={scopeApi.parties}
            canonicalTrades={canonicalTrades}
            tradeGroupByItem={tradeGroupByItem}
            chapterByItem={chapterByItem}
            subtradeByItem={subtradeByItem}
            onUpdate={handleGridUpdate}
            onDelete={deleteItemDirty}
            onDuplicate={duplicateItemDirty}
            onBeforeBulk={() => autosave.beforeBulk('Auto — before bulk delete')}
            onBeforeDuplicate={() => autosave.beforeBulk('Auto — before duplicate')}
            onMeasure={handleOpenTakeoff}
            onRates={(id) => setRatesLineId(id)}
            onSelectOption={handleSelectOption}
            currency={project.currency}
            billView={billView}
            onBillViewChange={setBillView}
            usesCostSplit={project.usesCostSplit}
            onCollapseControls={setCollapseCtl}
          />
          {measureLine && (
            <MeasurementSheet
              line={measureLine}
              zoneTree={zoneTree}
              measurements={measurements}
              onBasisChange={handleBasisChange}
              onCreate={handleMeasurementCreate}
              onUpdate={handleMeasurementUpdate}
              onRemoveRow={handleRemoveRow}
              onCreateRows={handleCreateRows}
              onDeleteMany={handleDeleteMany}
              onApplyPaste={handleApplyPaste}
              onToggleDeduct={handleToggleDeduct}
              onLinkExisting={() => setShowLinkModal(true)}
              onExport={handleExportMeasurements}
              onImport={() => setShowImportMeas(true)}
              onClose={() => setMeasureLineId(null)}
            />
          )}
        </>
      )}

      {showChooser && (
        <AddLineChooserModal
          onClose={() => setShowChooser(false)}
          onSelect={handleChooseLineType}
        />
      )}
      {showAdd && (
        <AddProjectItemModal
          versions={pickableVersions}
          zoneTree={zoneTree}
          onClose={() => setShowAdd(false)}
          onCreate={createItemAutosaved}
        />
      )}
      {showLump && (
        <AddLumpItemModal
          onClose={() => setShowLump(false)}
          onCreate={createItemAutosaved}
        />
      )}
      {showAllowance && (
        <AddAllowanceLineModal
          catalogue={subtradeCatalogue}
          catalogueLoading={catalogueLoading}
          zones={zones || []}
          defaultSequenceOrder={items.reduce((m, i) => Math.max(m, i.sequenceOrder || 0), 0) + 1}
          onClose={() => setShowAllowance(false)}
          onCreate={createItemAutosaved}
          onSubtradeCreated={async () => { await Promise.all([refreshCatalogue(), refreshTaxonomies()]); }}
        />
      )}
      {showImport && (
        <ImportItemsModal
          versions={pickableVersions}
          zonesById={zonesById}
          onClose={() => setShowImport(false)}
          onImport={createManyAutosaved}
        />
      )}
      {showPaste && (
        <PasteRowsModal
          versions={pickableVersions}
          zonesById={zonesById}
          onClose={() => setShowPaste(false)}
          onImport={createManyAutosaved}
        />
      )}
      {showLinkModal && measureLine && (
        <LinkMeasurementsModal
          line={measureLine}
          basis={measureLine.measureBasis || ''}
          measurements={measurements}
          codeForItem={codeForItem}
          onClose={() => setShowLinkModal(false)}
          onLink={handleLinkExisting}
        />
      )}
      {showImportMeas && measureLine && (
        <ImportMeasurementsModal
          line={measureLine}
          zonesById={zonesById}
          onClose={() => setShowImportMeas(false)}
          onImport={handleImportMeasurements}
        />
      )}
      {showScope && (
        <ScopeModal
          parties={scopeApi.parties}
          trades={trades}
          onAddParty={scopeApi.addParty}
          onUpdateParty={scopeApi.updateParty}
          onDeleteParty={scopeApi.deleteParty}
          onAssign={handleAssignScope}
          onClose={() => setShowScope(false)}
        />
      )}
      {ratesLine && (
        ratesLine.lineType === 'allowance' && isAreaBasis(ratesLine.measureBasis) ? (
          <SdRatePickerModal
            line={ratesLine}
            currency={project.currency}
            billView={billView}
            onClose={() => setRatesLineId(null)}
            onApplyRate={async (rate, target) => {
              await updateItem(ratesLine.id, target === 'billed' ? { rateBilled: rate } : { rateQuoted: rate });
              setRatesLineId(null);
            }}
          />
        ) : (
          <RateLibraryModal
            line={ratesLine}
            currency={project.currency}
            billView={billView}
            onClose={() => setRatesLineId(null)}
            onApplyRate={async (rate, target) => {
              await updateItem(ratesLine.id, target === 'billed' ? { rateBilled: rate } : { rateQuoted: rate });
              setRatesLineId(null);
            }}
          />
        )
      )}
      {showSeed && (
        <SeedFromTemplateModal
          project={project}
          taxonomies={seedTaxonomies}
          onClose={() => setShowSeed(false)}
          onSeeded={async () => { await refresh(); }}
        />
      )}
    </BrioCard>
  );
}

export default ProjectItemsSection;
